import axios from "axios";
import { loadRistaMenuFromAPI } from "./ristaAdapter";
import { cacheBranches, getCachedBranches } from "./indexedDBUtils";
import { isHiddenFilterTag, isHiddenTag } from "./config/hiddenTags";

const API_BASE_URL = "https://api.bluetokaicoffee.com/api";
const GRAPHQL_ENDPOINT ="https://api.bluetokaicoffee.com/graphql";
const RISTA_API_BASE_URL = "https://rdx8azn9lh.execute-api.ap-south-1.amazonaws.com/default/rista-tab-menu-api";

// Flag to switch between Blue Tokai API and Rista API
const USE_RISTA_API = true;

// New function to fetch cafe list from Rista API
export const fetchCafeList = async () => {
  try {
    // Try to get cached branches first
    const cachedBranches = await getCachedBranches();
    if (cachedBranches && cachedBranches.length > 0) {
      return cachedBranches;
    }

    // If cache is empty or expired, fetch from API
    const url = `${RISTA_API_BASE_URL}?requestType=branches`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch cafe list: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Transform Rista branch data to cafe format
    // Rista returns: { success: boolean, data: [{ branchName, branchCode, latitude, longitude }] }
    // Transform to: [{ branchCode, name, ristaId, cafeCode, lat, lng }]
    if (data.data && Array.isArray(data.data)) {
      const transformedBranches = data.data.map(branch => ({
        branchCode: branch.branchCode,  // Include for IndexedDB key path
        name: branch.branchName,
        ristaId: branch.branchCode,
        cafeCode: branch.branchCode,
        lat: branch.latitude,   // For location-based sorting
        lng: branch.longitude,  // For location-based sorting
      }));
      
      // Cache the branches
      await cacheBranches(transformedBranches);
      
      return transformedBranches;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching cafe list:", error);
    
    // Try to return cached data even if expired when API fails
    const cachedBranches = await getCachedBranches();
    if (cachedBranches && cachedBranches.length > 0) {
      return cachedBranches;
    }
    
    throw error;
  }
};

// Updated function to fetch menu list - now accepts cafeCode
export const fetchMenuList = async (cafeCode = "245") => {
  // Use Rista API (dynamic API call) if flag is enabled
  if (USE_RISTA_API) {
    try {
      return await loadRistaMenuFromAPI(RISTA_API_BASE_URL, cafeCode);
    } catch (error) {
      console.error("Error loading Rista menu:", error);
      return { categories: [], tags: [], productNames: [], store: null };
    }
  }

  // Original Blue Tokai API implementation
  try {
    const response = await axios.post(
      `${API_BASE_URL}/tab-menu-list`,
      { cafeCode: cafeCode }, // Use dynamic cafeCode based on selected cafe
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const categories = response.data?.data?.appCategories || [];
    const allTags = response.data?.data?.allTags || [];
    const store = response.data?.data?.store || null;
    const productNames = [];

    // Format tags
    const formattedTags = allTags
      .filter((tag) => !isHiddenFilterTag(tag))
      .map((tag) => ({
        id: tag.id,
        name: tag.name,
        description: "",
        categoryImage: tag.icon?.fullPath || "",
        products: [],
        isTag: true,
      }));

    // Format categories (parent only for menu, but include subcategory info in products)
    const formattedCategories = categories.map((cat) => {
      const allProducts = [];
      let subCategories = [];
      // Products from subcategories
      (cat.data || []).forEach((subCat) => {
        subCategories.push({
          name: subCat.categoryName,
          id: subCat.id
        });
        const subCatProducts = (subCat.products || []).map((p) => {
          productNames.push(p.title.toLowerCase());
          return ({
          id: p.id,
          name: p.title,
          price: p.price || 0,
          imageUrl: p.image?.fullPath || p.productDefaultImage || "",
          description: p.description || "",
          nutritions: p.nutritions || [],
          variations: p.variations || [],
          isVeg: p.isVeg,
          tags: (p.tags || []).filter((tag) => !isHiddenTag(tag)),
          subCategoryName: subCat.categoryName, // subcategory info
        })
      });
        allProducts.push(...subCatProducts);
      });

      // Products directly under parent category
      const parentProducts = (cat.products || []).map((p) => ({
        id: p.id,
        name: p.title,
        price: p.variations?.[0]?.price || p.price || 0,
        imageUrl: p.image?.fullPath || p.productDefaultImage || "",
        description: p.description || "",
        nutritions: p.nutritions || [],
        variations: p.variations || [],
        isVeg: p.isVeg,
        tags: (p.tags || []).filter((tag) => !isHiddenTag(tag)),
        subCategoryName: p.subCategoryName || null,
      }));

      return {
        id: cat.id,
        subCategories,
        parentName: cat.categoryName, // only parent name
        description: cat.description || "",
        categoryImage: cat.categoryImage?.fullPath || "",
        products: [...parentProducts, ...allProducts],
      };
    });

    return {
      categories: formattedCategories,
      tags: formattedTags,
      productNames: productNames,
      store: store,
    };
  } catch (error) {
    console.error("Error fetching menu list:", error);
    return { categories: [], tags: [], productNames: [], store: null };
  }
};

// Dine-in tables of one cafe, for the cart's table picker. Served by the same Lambda as the menu
// (requestType=tables) from Rista's table list for the branch.
// Resolves to [{ id, name, number }] sorted by number; an empty array means the cafe has no tables set up.
// Throws when the list could not be loaded, so the caller can offer a retry.
export const fetchCafeTables = async (cafeCode) => {
  const code = String(cafeCode || "").trim();
  if (!code) throw new Error("cafe code is required");
  const response = await fetch(`${RISTA_API_BASE_URL}?requestType=tables&branchCode=${encodeURIComponent(code)}`);
  if (!response.ok) throw new Error(`Failed to fetch tables: ${response.status}`);
  const data = await response.json();
  if (!data?.success || !Array.isArray(data?.data?.tables)) throw new Error("Unexpected tables response");
  return data.data.tables
    .filter((t) => t && /^\d{1,3}$/.test(String(t.number)))
    .map((t) => ({ id: String(t.id ?? ""), name: String(t.name || `Table ${t.number}`), number: String(t.number) }));
};

// New function to fetch sold-out items
export const fetchSoldOutItems = async (branch = "CA-096") => {
  try {
    const url = `${RISTA_API_BASE_URL}?branch=${branch}&requestType=soldout`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch sold-out items: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Transform response to a map of skuCode -> item info for easy lookup
    // Response structure: { success: boolean, data: { data: [{ skuCode, shortName, longName }] } }
    if (data.success && data.data && data.data.data && Array.isArray(data.data.data)) {
      const soldOutMap = {};
      data.data.data.forEach(item => {
        soldOutMap[item.skuCode] = {
          shortName: item.shortName,
          longName: item.longName,
          skuCode: item.skuCode
        };
      });
      return soldOutMap;
    }
    
    return {};
  } catch (error) {
    console.error("Error fetching sold-out items:", error);
    return {}; // Return empty map on error, don't break the app
  }
};

const detectMediaType = (url = "", declaredType = "") => {
  const normalizedDeclared = declaredType.toLowerCase();

  if (normalizedDeclared.includes("video")) {
    return "video";
  }

  if (normalizedDeclared.includes("image")) {
    return "image";
  }

  const cleanedUrl = url.split("?")[0];
  const extension = cleanedUrl.split(".").pop()?.toLowerCase();

  if (["mp4", "webm", "ogg", "mov", "m4v"].includes(extension)) {
    return "video";
  }

  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "avif", "jfif"].includes(extension)) {
    return "image";
  }

  return "unknown";
};

export const fetchBanners = async ({
  cafeCode = "0"
} = {}) => {
  try {
    const response = await axios.post(
      GRAPHQL_ENDPOINT,
      {
        query: `
          query($cafeCode:String!){
            getBannersListTab(cafeCode:$cafeCode){
              status
              data{
                banner{
                  id
                  title
                  duration
                  redirectMode
                  preference
                  option
                  displayOn
                  imageType
                  isActive
                  mainCategory {
                    id
                    categoryName
                  }
                  category {
                    id
                    categoryName
                  }
                  bannerImage{
                    id
                    fullPath
                    dominantColor
                  }
                  product{
                    id
                    title
                    variations {
                      masterProduct {
                        ristaSkuCode
                      }
                    }
                  }
                  offer{
                    id
                    name
                  }
                  stores{
                    id
                    name
                  }
                  cities {
                    id
                    cityName
                  }
                }
              }
              message
            }
          }
        `,
        variables: {
          cafeCode,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
          "device_id": "TAB_MENU"
        },
      }
    );

    const banners =
      response.data?.data?.getBannersListTab?.data?.banner || [];

    return banners.map((banner) => {
      const mediaUrl = banner.bannerImage?.fullPath || "";
      const mediaType = detectMediaType(mediaUrl, banner.imageType || "");

      return {
        id: banner.id,
        duration : banner.duration,
        title: banner.title,
        category: banner.mainCategory,
        subCategory: banner.category,
        redirectMode: banner.redirectMode,
        preference: banner.preference,
        option: banner.option,
        displayOn: banner.displayOn,
        imageType: banner.imageType,
        isActive: banner.isActive,
        mediaUrl,
        mediaType,
        image: mediaUrl,
        dominantColor: banner.bannerImage?.dominantColor,
        product: banner.product,
        offer: banner.offer,
        stores: banner.stores,
        cities: banner.cities,
      };
    });
  } catch (error) {
    console.error("Error fetching banners:", error);
    return [];
  }
};
